/**
 * ProofOfChange
 * Before-vs-now visualization of clinical scores, ERI, and program progress.
 * The single most important screen for retention and investor demos.
 * Now includes: Cognitive Pulse — objective neuroplasticity measurement.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Spark } from '@/components/HypIcons';
import {
  LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, ReferenceLine,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Severity helpers ──────────────────────────────────────────────────────────
function phqLevel(s) {
  if (s == null) return { label: '—', color: '#CCC', bg: '#F5F5F5', pct: 0 };
  if (s <= 4)  return { label: 'Minimal',      color: '#2D8B87', bg: 'rgba(45,139,135,0.08)',  pct: Math.round((s / 27) * 100) };
  if (s <= 9)  return { label: 'Mild',          color: '#7BAA72', bg: 'rgba(123,170,114,0.08)', pct: Math.round((s / 27) * 100) };
  if (s <= 14) return { label: 'Moderate',      color: '#C9983E', bg: 'rgba(201,152,62,0.08)',  pct: Math.round((s / 27) * 100) };
  if (s <= 19) return { label: 'Mod-Severe',    color: '#B86B30', bg: 'rgba(184,107,48,0.08)',  pct: Math.round((s / 27) * 100) };
  return              { label: 'Severe',         color: '#A33B2B', bg: 'rgba(163,59,43,0.08)',   pct: Math.round((s / 27) * 100) };
}

function gadLevel(s) {
  if (s == null) return { label: '—', color: '#CCC', bg: '#F5F5F5', pct: 0 };
  if (s <= 4)  return { label: 'Minimal',  color: '#2D8B87', bg: 'rgba(45,139,135,0.08)',  pct: Math.round((s / 21) * 100) };
  if (s <= 9)  return { label: 'Mild',     color: '#7BAA72', bg: 'rgba(123,170,114,0.08)', pct: Math.round((s / 21) * 100) };
  if (s <= 14) return { label: 'Moderate', color: '#C9983E', bg: 'rgba(201,152,62,0.08)',  pct: Math.round((s / 21) * 100) };
  return              { label: 'Severe',   color: '#A33B2B', bg: 'rgba(163,59,43,0.08)',   pct: Math.round((s / 21) * 100) };
}

function delta(first, last) {
  if (first == null || last == null) return null;
  return last - first;
}

function deltaLabel(d, lowerIsBetter = true) {
  if (d == null) return null;
  if (d === 0) return { text: 'unchanged', color: '#999' };
  const improved = lowerIsBetter ? d < 0 : d > 0;
  return {
    text: `${d > 0 ? '+' : ''}${d} pts · ${improved ? 'improved' : 'worsened'}`,
    color: improved ? '#2D8B87' : '#A33B2B',
  };
}

// ── ScoreBar — animated horizontal bar ───────────────────────────────────────
function ScoreBar({ pct, color }) {
  return (
    <div style={{ height: '6px', background: 'rgba(0,0,0,0.06)', borderRadius: '3px', overflow: 'hidden', marginTop: '8px' }}>
      <div style={{
        height: '100%',
        width: `${pct}%`,
        background: color,
        borderRadius: '3px',
        transition: 'width 1s ease',
      }} />
    </div>
  );
}

// ── BeforeAfterCard — single metric split ─────────────────────────────────────
function BeforeAfterCard({ title, subtitle, first, latest, levelFn, maxLabel }) {
  const fLevel = levelFn(first?.score);
  const lLevel = levelFn(latest?.score);
  const d = delta(first?.score, latest?.score);
  const dl = deltaLabel(d, true);
  const hasBoth = first != null && latest != null && first !== latest;

  return (
    <div style={{
      background: 'rgba(255,255,255,0.92)',
      borderRadius: '18px',
      padding: '20px',
      marginBottom: '14px',
      boxShadow: '0 2px 16px rgba(30,107,104,0.07)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '16px' }}>
        <div>
          <p style={{ margin: 0, fontFamily: "'Playfair Display', serif", fontSize: '1rem', color: '#1E3A3A' }}>{title}</p>
          <p style={{ margin: '2px 0 0', fontSize: '0.72rem', color: '#9AADAB', letterSpacing: '0.04em' }}>{subtitle}</p>
        </div>
        {hasBoth && dl && (
          <span style={{
            fontSize: '0.72rem', fontWeight: '700', color: dl.color,
            background: dl.color + '18', padding: '3px 10px', borderRadius: '20px',
          }}>
            {dl.text}
          </span>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '12px', alignItems: 'center' }}>
        {/* Before */}
        <div style={{ padding: '14px', background: fLevel.bg, borderRadius: '12px', textAlign: 'center' }}>
          <p style={{ margin: '0 0 2px', fontSize: '0.65rem', fontWeight: '700', color: '#AAB8B6', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            {first?.date ? 'Started' : 'No data'}
          </p>
          {first?.score != null
            ? <>
                <p style={{ margin: '4px 0 2px', fontSize: '1.6rem', fontWeight: '700', color: fLevel.color, lineHeight: 1 }}>{first.score}</p>
                <p style={{ margin: 0, fontSize: '0.72rem', color: fLevel.color, fontWeight: '600' }}>{fLevel.label}</p>
                <ScoreBar pct={fLevel.pct} color={fLevel.color} />
              </>
            : <p style={{ margin: '12px 0 0', fontSize: '0.82rem', color: '#CCC' }}>—</p>
          }
          {first?.date && <p style={{ margin: '8px 0 0', fontSize: '0.65rem', color: '#C0CECE' }}>{first.date}</p>}
        </div>

        {/* Arrow */}
        <div style={{ textAlign: 'center', color: '#B8D3CF' }}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M4 10 H16 M12 6 L16 10 L12 14" stroke="#B8D3CF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>

        {/* After */}
        <div style={{ padding: '14px', background: lLevel.bg, borderRadius: '12px', textAlign: 'center' }}>
          <p style={{ margin: '0 0 2px', fontSize: '0.65rem', fontWeight: '700', color: '#AAB8B6', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            {latest?.date ? 'Today' : 'No data'}
          </p>
          {latest?.score != null
            ? <>
                <p style={{ margin: '4px 0 2px', fontSize: '1.6rem', fontWeight: '700', color: lLevel.color, lineHeight: 1 }}>{latest.score}</p>
                <p style={{ margin: 0, fontSize: '0.72rem', color: lLevel.color, fontWeight: '600' }}>{lLevel.label}</p>
                <ScoreBar pct={lLevel.pct} color={lLevel.color} />
              </>
            : <p style={{ margin: '12px 0 0', fontSize: '0.82rem', color: '#CCC' }}>—</p>
          }
          {latest?.date && <p style={{ margin: '8px 0 0', fontSize: '0.65rem', color: '#C0CECE' }}>{latest.date}</p>}
        </div>
      </div>

      {first == null && latest == null && (
        <p style={{ margin: '12px 0 0', fontSize: '0.8rem', color: '#BBB', textAlign: 'center' }}>
          Complete a clinical assessment to track {title} over time.
        </p>
      )}

      <p style={{ margin: '10px 0 0', fontSize: '0.68rem', color: '#C0CECE', textAlign: 'right' }}>{maxLabel}</p>
    </div>
  );
}

// ── Custom ERI tooltip ─────────────────────────────────────────────────────────
function ERITooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid rgba(30,107,104,0.15)', borderRadius: '10px', padding: '10px 14px', fontSize: '0.78rem' }}>
      <p style={{ margin: '0 0 4px', color: '#888' }}>{label}</p>
      <p style={{ margin: 0, color: '#1E6B68', fontWeight: '700' }}>ERI {payload[0].value}</p>
    </div>
  );
}

// ── StatPill ──────────────────────────────────────────────────────────────────
function StatPill({ value, label, color = '#1E6B68', sub }) {
  return (
    <div style={{ textAlign: 'center', flex: 1 }}>
      <p style={{ margin: 0, fontSize: '1.55rem', fontWeight: '700', color, lineHeight: 1 }}>{value}</p>
      {sub && <p style={{ margin: '2px 0 0', fontSize: '0.65rem', color: color + 'AA', fontWeight: '600' }}>{sub}</p>}
      <p style={{ margin: '4px 0 0', fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{label}</p>
    </div>
  );
}

// ── Demo fallback data (renders when backend offline) ─────────────────────────
const DEMO = {
  assessments: [
    { phq9_total: 15, gad7_total: 12, created_at: '2026-01-15T10:00:00Z' },
    { phq9_total: 10, gad7_total: 8,  created_at: '2026-02-15T10:00:00Z' },
    { phq9_total: 6,  gad7_total: 5,  created_at: '2026-03-15T10:00:00Z' },
    { phq9_total: 4,  gad7_total: 3,  created_at: '2026-04-15T10:00:00Z' },
  ],
  eri: Array.from({ length: 14 }, (_, i) => ({
    eri_score: 42 + Math.round(Math.sin(i * 0.6) * 8 + i * 2.1),
    calculated_at: new Date(Date.now() - (13 - i) * 86400000).toISOString(),
  })),
  daysActive: 94,
};

// ─────────────────────────────────────────────────────────────────────────────
// COGNITIVE PULSE — Emotional Reaction Time Test
// Measures how quickly the nervous system processes emotional vs neutral stimuli.
// Lower = faster processing = less emotional interference.
// ─────────────────────────────────────────────────────────────────────────────
const EMOTIONAL_WORDS  = ['Fear', 'Shame', 'Grief', 'Rage', 'Panic', 'Dread', 'Hurt', 'Guilt'];
const NEUTRAL_WORDS    = ['Table', 'Cloud', 'Stone', 'Paper', 'River', 'Glass', 'Chair', 'Floor'];
const TOTAL_TRIALS     = 12;

function CognitivePulse({ userId, onComplete }) {
  const TEAL = '#2D8B87';
  const DARK = '#1E6B68';
  const CREAM = 'rgba(247,243,236,0.97)';

  const [phase, setPhase]         = useState('intro');      // intro | fixation | word | feedback | done
  const [trialIndex, setTrialIndex] = useState(0);
  const [trials, setTrials]       = useState([]);
  const [currentWord, setCurrentWord] = useState(null);
  const [currentType, setCurrentType] = useState(null);
  const [wordShownAt, setWordShownAt] = useState(null);
  const [results, setResults]     = useState([]);
  const [feedback, setFeedback]   = useState(null);
  const [saving, setSaving]       = useState(false);
  const [taskHistory, setTaskHistory] = useState([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (userId) fetchHistory();
    return () => clearTimeout(timerRef.current);
  }, [userId]);

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API}/cognitive-tasks/${userId}?task_type=reaction_time`);
      setTaskHistory(res.data.results || []);
      setHistoryLoaded(true);
    } catch (e) { setHistoryLoaded(true); }
  };

  // Build randomised trial list once
  const buildTrials = useCallback(() => {
    const pool = [
      ...EMOTIONAL_WORDS.slice(0, 6).map(w => ({ word: w, type: 'emotional' })),
      ...NEUTRAL_WORDS.slice(0, 6).map(w => ({ word: w, type: 'neutral' })),
    ].sort(() => Math.random() - 0.5);
    setTrials(pool);
    return pool;
  }, []);

  const startTask = () => {
    const pool = buildTrials();
    setResults([]);
    setTrialIndex(0);
    setPhase('fixation');
    showTrial(pool, 0);
  };

  const showTrial = (pool, idx) => {
    if (idx >= pool.length) { finishTask(); return; }
    setPhase('fixation');
    timerRef.current = setTimeout(() => {
      const trial = pool[idx];
      setCurrentWord(trial.word);
      setCurrentType(trial.type);
      setWordShownAt(performance.now());
      setPhase('word');
    }, 600 + Math.random() * 400);
  };

  const handleResponse = (response) => {
    if (phase !== 'word') return;
    const rt = Math.round(performance.now() - wordShownAt);
    const correct = (response === 'emotional') === (currentType === 'emotional');
    const newResult = { word: currentWord, type: currentType, response, rt, correct };
    const updatedResults = [...results, newResult];
    setResults(updatedResults);
    setFeedback({ correct, rt });
    setPhase('feedback');
    timerRef.current = setTimeout(() => {
      setFeedback(null);
      const nextIdx = trialIndex + 1;
      setTrialIndex(nextIdx);
      showTrial(trials, nextIdx);
    }, 500);
  };

  const finishTask = async () => {
    setPhase('done');
    if (!userId) return;

    const completed = results.filter(r => r.correct);
    const avgRt = completed.length > 0
      ? Math.round(completed.reduce((a, r) => a + r.rt, 0) / completed.length) : null;
    const accuracy = Math.round((completed.length / results.length) * 100);

    const emotionalRts = completed.filter(r => r.type === 'emotional').map(r => r.rt);
    const neutralRts   = completed.filter(r => r.type === 'neutral').map(r => r.rt);
    const avgEmotional = emotionalRts.length ? Math.round(emotionalRts.reduce((a,b) => a+b,0) / emotionalRts.length) : null;
    const avgNeutral   = neutralRts.length   ? Math.round(neutralRts.reduce((a,b) => a+b,0)   / neutralRts.length)   : null;

    setSaving(true);
    try {
      await axios.post(`${API}/cognitive-tasks/result`, {
        user_id: userId,
        task_type: 'reaction_time',
        score: avgRt,
        accuracy,
        avg_reaction_ms: avgRt,
        trials: results,
        meta: { avg_emotional_ms: avgEmotional, avg_neutral_ms: avgNeutral },
      });
      fetchHistory();
      onComplete?.({ avgRt, accuracy, avgEmotional, avgNeutral });
    } catch (e) {}
    setSaving(false);
  };

  // ── Compute summary from last completed result ──
  const lastResult = results.filter(r => r.correct);
  const avgRtFinal = lastResult.length
    ? Math.round(lastResult.reduce((a,r) => a + r.rt, 0) / lastResult.length) : null;
  const accuracyFinal = results.length
    ? Math.round((lastResult.length / results.length) * 100) : null;

  // History trend
  const historyTrend = taskHistory.length >= 2
    ? Math.round(((taskHistory[taskHistory.length-1].avg_reaction_ms - taskHistory[0].avg_reaction_ms) / taskHistory[0].avg_reaction_ms) * 100)
    : null;

  return (
    <div style={{
      background: CREAM, border: '1px solid rgba(30,107,104,0.10)',
      borderRadius: '20px', padding: '24px', marginBottom: '16px',
      boxShadow: '0 2px 16px rgba(30,107,104,0.07)',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
        <Spark size={18} color={TEAL} />
        <div>
          <div style={{ fontSize: '11px', fontWeight: 700, color: TEAL,
            letterSpacing: '0.08em', textTransform: 'uppercase' }}>Cognitive Pulse</div>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#1A2E2C' }}>
            Emotional Processing Speed
          </h3>
        </div>
        {taskHistory.length > 0 && (
          <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
            <div style={{ fontSize: '18px', fontWeight: 700, color: DARK }}>
              {taskHistory[taskHistory.length-1]?.avg_reaction_ms}ms
            </div>
            <div style={{ fontSize: '10px', color: '#8B9B97' }}>Last run</div>
          </div>
        )}
      </div>

      <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#5A7A76', lineHeight: 1.6 }}>
        Classify words as <strong>emotional</strong> or <strong>neutral</strong> as fast as you can.
        Your reaction time reveals how much cognitive load emotions place on your system — and how that changes with practice.
      </p>

      {/* History trend */}
      {historyTrend != null && (
        <div style={{
          background: historyTrend < 0 ? 'rgba(45,139,135,0.08)' : 'rgba(184,144,106,0.08)',
          border: `1px solid ${historyTrend < 0 ? 'rgba(45,139,135,0.20)' : 'rgba(184,144,106,0.20)'}`,
          borderRadius: '10px', padding: '10px 14px', marginBottom: '16px',
          fontSize: '13px', fontWeight: 600,
          color: historyTrend < 0 ? DARK : '#7A4A20',
        }}>
          {historyTrend < 0
            ? `↓ ${Math.abs(historyTrend)}% faster since first test — your nervous system is processing more efficiently.`
            : `↑ ${Math.abs(historyTrend)}% slower than your first test — this is normal during periods of high load.`}
          <span style={{ fontWeight: 400, color: '#8B9B97', marginLeft: '4px' }}>
            ({taskHistory.length} tests total)
          </span>
        </div>
      )}

      {/* ── INTRO ── */}
      {phase === 'intro' && (
        <div style={{ textAlign: 'center' }}>
          <div style={{
            background: 'rgba(30,107,104,0.04)', border: '1px solid rgba(30,107,104,0.10)',
            borderRadius: '14px', padding: '18px', marginBottom: '18px', textAlign: 'left',
          }}>
            <p style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: 600, color: '#1A2E2C' }}>How it works:</p>
            <ol style={{ margin: 0, padding: '0 0 0 18px', fontSize: '13px', color: '#4A6A66', lineHeight: 1.8 }}>
              <li>A word appears on screen</li>
              <li>Tap <strong>Emotional</strong> or <strong>Neutral</strong> as fast as you can</li>
              <li>12 trials · takes ~45 seconds</li>
            </ol>
          </div>
          <button onClick={startTask} style={{
            background: `linear-gradient(135deg, ${DARK}, ${TEAL})`,
            color: '#fff', border: 'none', borderRadius: '14px',
            padding: '13px 32px', fontSize: '15px', fontWeight: 600,
            cursor: 'pointer', boxShadow: '0 4px 14px rgba(30,107,104,0.25)',
          }}>
            Start test →
          </button>
        </div>
      )}

      {/* ── FIXATION ── */}
      {phase === 'fixation' && (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <div style={{ fontSize: '32px', color: TEAL, fontWeight: 300 }}>+</div>
          <p style={{ fontSize: '12px', color: '#A0B4B0', marginTop: '8px' }}>
            Trial {trialIndex + 1} / {trials.length}
          </p>
        </div>
      )}

      {/* ── WORD ── */}
      {phase === 'word' && (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div style={{
            fontSize: '36px', fontWeight: 800, color: '#1A2E2C',
            letterSpacing: '-0.5px', marginBottom: '24px',
            animation: 'fadeIn 0.1s ease',
          }}>
            {currentWord}
          </div>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <button onClick={() => handleResponse('emotional')} style={{
              flex: 1, maxWidth: '180px', padding: '14px',
              background: 'rgba(163,59,43,0.08)', border: '2px solid rgba(163,59,43,0.25)',
              borderRadius: '14px', fontSize: '15px', fontWeight: 700,
              color: '#A33B2B', cursor: 'pointer', transition: 'all 0.1s',
            }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="7.5" stroke="#A33B2B" strokeWidth="1.5"/><path d="M7 7.5 C7 6.5 8 6 8.5 7" stroke="#A33B2B" strokeWidth="1.3" strokeLinecap="round"/><path d="M13 7.5 C13 6.5 12 6 11.5 7" stroke="#A33B2B" strokeWidth="1.3" strokeLinecap="round"/><path d="M7.5 13.5 C8.5 12 11.5 12 12.5 13.5" stroke="#A33B2B" strokeWidth="1.3" strokeLinecap="round"/><path d="M10 11 L10 9" stroke="#A33B2B" strokeWidth="1.3" strokeLinecap="round"/></svg>
                Emotional
              </span>
            </button>
            <button onClick={() => handleResponse('neutral')} style={{
              flex: 1, maxWidth: '180px', padding: '14px',
              background: 'rgba(45,139,135,0.08)', border: '2px solid rgba(45,139,135,0.25)',
              borderRadius: '14px', fontSize: '15px', fontWeight: 700,
              color: DARK, cursor: 'pointer', transition: 'all 0.1s',
            }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="7.5" stroke={DARK} strokeWidth="1.5"/><path d="M7.5 8 C7.5 7 8.5 6.5 9 7.5" stroke={DARK} strokeWidth="1.3" strokeLinecap="round"/><path d="M12.5 8 C12.5 7 11.5 6.5 11 7.5" stroke={DARK} strokeWidth="1.3" strokeLinecap="round"/><path d="M7.5 13 Q10 11.5 12.5 13" stroke={DARK} strokeWidth="1.3" strokeLinecap="round" fill="none"/></svg>
                Neutral
              </span>
            </button>
          </div>
          <p style={{ fontSize: '11px', color: '#C0CECE', marginTop: '14px' }}>
            Trial {trialIndex + 1} / {trials.length}
          </p>
        </div>
      )}

      {/* ── FEEDBACK ── */}
      {phase === 'feedback' && feedback && (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <div style={{ fontSize: '32px' }}>{feedback.correct ? '✓' : '✗'}</div>
          <p style={{ fontSize: '13px', color: feedback.correct ? TEAL : '#A33B2B', marginTop: '6px' }}>
            {feedback.rt}ms
          </p>
        </div>
      )}

      {/* ── DONE ── */}
      {phase === 'done' && (
        <div style={{ textAlign: 'center' }}>
          <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'center' }}>
            <Spark size={36} color={DARK} />
          </div>
          {avgRtFinal && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '40px', fontWeight: 800, color: DARK, letterSpacing: '-1px' }}>
                {avgRtFinal}ms
              </div>
              <div style={{ fontSize: '13px', color: '#6B8B87', marginTop: '4px' }}>
                average reaction time · {accuracyFinal}% accuracy
              </div>
            </div>
          )}
          {avgRtFinal && (
            <div style={{
              background: 'rgba(30,107,104,0.06)', borderRadius: '12px',
              padding: '14px', marginBottom: '16px', fontSize: '13px', color: '#2C4A47', lineHeight: 1.6,
            }}>
              {avgRtFinal < 500
                ? 'Excellent processing speed. Your nervous system distinguishes emotional from neutral stimuli with minimal interference.'
                : avgRtFinal < 700
                ? 'Good emotional processing. A regulated nervous system typically scores in this range.'
                : 'Your nervous system is working harder to process emotional content. This is common during periods of stress — it improves with regular practice.'}
            </div>
          )}
          {saving && <p style={{ fontSize: '13px', color: '#8B9B97' }}>Saving results…</p>}
          <button onClick={() => { setPhase('intro'); setResults([]); setTrialIndex(0); }} style={{
            background: 'rgba(30,107,104,0.08)', border: '1px solid rgba(30,107,104,0.20)',
            borderRadius: '12px', padding: '10px 24px',
            fontSize: '13px', fontWeight: 600, color: DARK, cursor: 'pointer',
          }}>
            Run again
          </button>
        </div>
      )}
    </div>
  );
}


// ── Main component ────────────────────────────────────────────────────────────
export default function ProofOfChange() {
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [eriHistory, setEriHistory] = useState([]);
  const [daysActive, setDaysActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDemo, setIsDemo] = useState(false);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    const raw = localStorage.getItem('user');
    if (!raw) { loadDemo(); return; }
    const { user_id, created_at } = JSON.parse(raw);
    setUserId(user_id);
    if (created_at) {
      const days = Math.floor((Date.now() - new Date(created_at)) / 86400000);
      setDaysActive(days);
    }
    loadData(user_id);
  }, []);

  const loadDemo = () => {
    setAssessments(DEMO.assessments);
    setEriHistory(DEMO.eri);
    setDaysActive(DEMO.daysActive);
    setIsDemo(true);
    setLoading(false);
  };

  const loadData = async (userId) => {
    try {
      const [aRes, eRes] = await Promise.allSettled([
        axios.get(`${API}/assessments/${userId}`),
        axios.get(`${API}/training/eri-history/${userId}?limit=30`),
      ]);
      const aData = aRes.status === 'fulfilled' ? aRes.value.data?.assessments : null;
      const eData = eRes.status === 'fulfilled' ? eRes.value.data?.scores : null;
      // Both failed or empty → show demo
      if ((!aData || aData.length === 0) && !eData) { loadDemo(); return; }
      setAssessments(Array.isArray(aData) ? aData : []);
      setEriHistory(Array.isArray(eData) ? eData : []);
    } catch {
      loadDemo();
      return;
    }
    setLoading(false);
  };

  // ── Derived data ────────────────────────────────────────────────────────────
  const sorted = [...assessments].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  const first = sorted[0];
  const latest = sorted[sorted.length - 1];

  const fmtDate = (iso) => iso
    ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;

  const phqFirst  = first  ? { score: first.phq9_score  ?? first.phq9_total,  date: fmtDate(first.created_at)  } : null;
  const phqLatest = latest ? { score: latest.phq9_score ?? latest.phq9_total, date: fmtDate(latest.created_at) } : null;
  const gadFirst  = first  ? { score: first.gad7_score  ?? first.gad7_total,  date: fmtDate(first.created_at)  } : null;
  const gadLatest = latest ? { score: latest.gad7_score ?? latest.gad7_total, date: fmtDate(latest.created_at) } : null;

  const eriChartData = [...eriHistory]
    .sort((a, b) => new Date(a.calculated_at) - new Date(b.calculated_at))
    .map((e) => ({
      date: new Date(e.calculated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
      eri: Math.round(e.eri_score),
    }));

  const eriFirst  = eriChartData[0]?.eri ?? null;
  const eriLatest = eriChartData[eriChartData.length - 1]?.eri ?? null;
  const eriDelta  = eriFirst != null && eriLatest != null ? eriLatest - eriFirst : null;

  const phqDelta = delta(phqFirst?.score, phqLatest?.score);
  const gadDelta = delta(gadFirst?.score, gadLatest?.score);

  // Summary banner metrics
  const summaryItems = [
    {
      value: daysActive != null ? daysActive : '—',
      label: 'Days',
      sub: 'with Hypseya',
      color: 'rgba(255,255,255,0.95)',
    },
    {
      value: eriDelta != null ? `${eriDelta > 0 ? '+' : ''}${eriDelta}` : '—',
      label: 'ERI',
      sub: 'change',
      color: eriDelta != null && eriDelta > 0 ? '#7EDEDA' : eriDelta != null && eriDelta < 0 ? '#F4A38A' : 'rgba(255,255,255,0.95)',
    },
    {
      value: phqDelta != null ? `${phqDelta > 0 ? '+' : ''}${phqDelta}` : '—',
      label: 'PHQ-9',
      sub: 'depression',
      color: phqDelta != null && phqDelta < 0 ? '#7EDEDA' : phqDelta != null && phqDelta > 0 ? '#F4A38A' : 'rgba(255,255,255,0.95)',
    },
  ];

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F7F3EC' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '50%', border: '2px solid rgba(30,107,104,0.2)', borderTopColor: '#1E6B68', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <p style={{ color: '#9AADAB', fontSize: '0.85rem' }}>Reading your data…</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #F7F3EC 0%, #EEF4F3 100%)', paddingBottom: '100px' }}>

      {/* ── Header ── */}
      <div style={{
        background: 'linear-gradient(160deg, #0D2B2A 0%, #1E6B68 100%)',
        padding: '52px 24px 36px',
        position: 'relative', overflow: 'hidden',
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{ position: 'absolute', top: '16px', left: '16px', background: 'rgba(255,255,255,0.1)', border: 'none', color: 'rgba(255,255,255,0.7)', borderRadius: '8px', padding: '6px 12px', fontSize: '0.8rem', cursor: 'pointer' }}
        >
          ← Back
        </button>

        {/* Decorative ripple */}
        <div style={{ position: 'absolute', right: '-30px', top: '-30px', width: '160px', height: '160px', borderRadius: '50%', border: '1px solid rgba(255,255,255,0.07)' }} />
        <div style={{ position: 'absolute', right: '-60px', top: '-60px', width: '220px', height: '220px', borderRadius: '50%', border: '1px solid rgba(255,255,255,0.04)' }} />

        {isDemo && (
          <div style={{ display: 'inline-block', background: 'rgba(255,255,255,0.12)', borderRadius: '20px', padding: '3px 12px', marginBottom: '12px' }}>
            <span style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.6)', fontWeight: '700', letterSpacing: '0.08em' }}>DEMO DATA</span>
          </div>
        )}

        <p style={{ margin: '0 0 4px', fontSize: '0.68rem', fontWeight: '700', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
          Your transformation
        </p>
        <h1 style={{ margin: '0 0 24px', fontFamily: "'Playfair Display', serif", color: '#fff', fontSize: '1.75rem', fontWeight: '400', lineHeight: 1.2 }}>
          Proof of Change
        </h1>

        {/* Summary pills */}
        <div style={{ display: 'flex', gap: '1px', background: 'rgba(255,255,255,0.08)', borderRadius: '16px', padding: '18px 12px' }}>
          {summaryItems.map((item, i) => (
            <StatPill key={i} {...item} />
          ))}
        </div>
      </div>

      {/* ── Content ── */}
      <div style={{ padding: '24px 18px 0', maxWidth: '500px', margin: '0 auto' }}>

        {/* ERI Timeline */}
        {eriChartData.length > 1 && (
          <div style={{ background: 'rgba(255,255,255,0.92)', borderRadius: '18px', padding: '20px', marginBottom: '14px', boxShadow: '0 2px 16px rgba(30,107,104,0.07)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
              <div>
                <p style={{ margin: 0, fontFamily: "'Playfair Display', serif", fontSize: '1rem', color: '#1E3A3A' }}>
                  Emotional Regulation Index
                </p>
                <p style={{ margin: '2px 0 0', fontSize: '0.72rem', color: '#9AADAB' }}>ERI · {eriChartData.length} recordings</p>
              </div>
              {eriLatest != null && (
                <div style={{ textAlign: 'right' }}>
                  <p style={{ margin: 0, fontSize: '1.4rem', fontWeight: '700', color: '#1E6B68', lineHeight: 1 }}>{eriLatest}</p>
                  <p style={{ margin: '2px 0 0', fontSize: '0.65rem', color: '#AAB8B6' }}>current</p>
                </div>
              )}
            </div>

            <ResponsiveContainer width="100%" height={140}>
              <LineChart data={eriChartData} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: '#C0CECE' }}
                  tickLine={false}
                  axisLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tick={{ fontSize: 10, fill: '#C0CECE' }}
                  tickLine={false}
                  axisLine={false}
                  domain={['auto', 'auto']}
                />
                <Tooltip content={<ERITooltip />} />
                {eriFirst != null && (
                  <ReferenceLine y={eriFirst} stroke="rgba(30,107,104,0.15)" strokeDasharray="4 4" />
                )}
                <Line
                  type="monotone"
                  dataKey="eri"
                  stroke="#1E6B68"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4, fill: '#1E6B68' }}
                />
              </LineChart>
            </ResponsiveContainer>

            {eriFirst != null && eriLatest != null && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid rgba(30,107,104,0.08)' }}>
                <span style={{ fontSize: '0.72rem', color: '#AAB8B6' }}>Started at <strong style={{ color: '#888' }}>{eriFirst}</strong></span>
                <span style={{ fontSize: '0.72rem', color: eriDelta > 0 ? '#2D8B87' : eriDelta < 0 ? '#A33B2B' : '#888' }}>
                  {eriDelta > 0 ? `↑ +${eriDelta} pts · growing` : eriDelta < 0 ? `↓ ${eriDelta} pts` : 'stable'}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Clinical Scores */}
        <p style={{ margin: '20px 0 12px', fontSize: '0.68rem', fontWeight: '700', color: '#AAB8B6', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
          Clinical Scores
        </p>

        <BeforeAfterCard
          title="Depression"
          subtitle="PHQ-9 · Patient Health Questionnaire"
          first={phqFirst}
          latest={sorted.length > 1 ? phqLatest : null}
          levelFn={phqLevel}
          maxLabel="Scale 0–27"
        />

        <BeforeAfterCard
          title="Anxiety"
          subtitle="GAD-7 · Generalized Anxiety Disorder"
          first={gadFirst}
          latest={sorted.length > 1 ? gadLatest : null}
          levelFn={gadLevel}
          maxLabel="Scale 0–21"
        />

        {sorted.length < 2 && sorted.length > 0 && (
          <div style={{ background: 'rgba(30,107,104,0.05)', border: '1px solid rgba(30,107,104,0.12)', borderRadius: '14px', padding: '16px 18px', marginBottom: '14px', textAlign: 'center' }}>
            <p style={{ margin: '0 0 8px', fontSize: '0.85rem', color: '#1E6B68', fontWeight: '600' }}>First assessment complete</p>
            <p style={{ margin: '0 0 12px', color: '#888', fontSize: '0.8rem', lineHeight: 1.5 }}>Come back in 4 weeks to see how much you've changed.</p>
            <button
              onClick={() => navigate('/assessment?followup=1')}
              style={{ padding: '10px 20px', background: '#1E6B68', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '0.82rem', fontWeight: '600', cursor: 'pointer' }}
            >
              Take follow-up assessment →
            </button>
          </div>
        )}

        {sorted.length === 0 && (
          <div style={{ background: 'rgba(30,107,104,0.04)', border: '1px dashed rgba(30,107,104,0.2)', borderRadius: '14px', padding: '24px', textAlign: 'center', marginBottom: '14px' }}>
            <p style={{ margin: '0 0 6px', fontSize: '0.9rem', color: '#1E6B68', fontWeight: '600' }}>No assessments yet</p>
            <p style={{ margin: '0 0 14px', color: '#888', fontSize: '0.8rem', lineHeight: 1.5 }}>A 5-minute baseline assessment unlocks your clinical tracking.</p>
            <button
              onClick={() => navigate('/assessment')}
              style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #1E6B68, #2D8B87)', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '0.82rem', fontWeight: '600', cursor: 'pointer' }}
            >
              Start baseline assessment →
            </button>
          </div>
        )}

        {/* Interpretation */}
        {(phqDelta != null || gadDelta != null) && sorted.length > 1 && (() => {
          const improved = (phqDelta != null && phqDelta < -3) || (gadDelta != null && gadDelta < -3);
          const stable   = !improved && (phqDelta != null || gadDelta != null);
          return (
            <div style={{ background: 'linear-gradient(135deg, rgba(30,107,104,0.06), rgba(45,139,135,0.04))', border: '1px solid rgba(30,107,104,0.12)', borderRadius: '16px', padding: '18px 20px', marginBottom: '14px' }}>
              <p style={{ margin: '0 0 6px', fontSize: '0.72rem', fontWeight: '700', color: '#1E6B68', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                What this means
              </p>
              <p style={{ margin: 0, color: '#444', fontSize: '0.88rem', lineHeight: 1.65 }}>
                {improved
                  ? 'Your clinical scores reflect meaningful improvement. The work you are doing is measurable — not just felt. This is the kind of change that compounds over time.'
                  : stable
                  ? 'Your scores are tracking. Stability during a period of active practice is itself a form of progress — the foundation before the shift.'
                  : 'Keep going. Clinical change takes time to register. What matters is that you showed up.'}
              </p>
            </div>
          );
        })()}

        {/* Disclaimer */}
        <p style={{ fontSize: '0.68rem', color: '#C0CECE', lineHeight: 1.6, textAlign: 'center', padding: '0 8px', marginTop: '8px' }}>
          PHQ-9 and GAD-7 are validated screening tools, not diagnostic instruments. Results should be reviewed with a qualified mental health professional.
        </p>

        {/* ── Cognitive Pulse ── */}
        {!isDemo && userId && (
          <>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              margin: '28px 0 14px', padding: '0 2px',
            }}>
              <div style={{ flex: 1, height: '1px', background: 'rgba(30,107,104,0.10)' }} />
              <span style={{
                fontSize: '11px', fontWeight: 700, color: '#8B9B97',
                letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap',
              }}>Neuroplasticity Measurement</span>
              <div style={{ flex: 1, height: '1px', background: 'rgba(30,107,104,0.10)' }} />
            </div>
            <CognitivePulse userId={userId} />
            <p style={{ fontSize: '0.68rem', color: '#C0CECE', lineHeight: 1.6, textAlign: 'center', padding: '0 8px', marginTop: '4px' }}>
              Reaction time to emotional vs neutral words is a validated proxy for emotional processing load. Based on the Emotional Stroop paradigm (Williams et al., 1996).
            </p>
          </>
        )}
      </div>
    </div>
  );
}
